import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { motion } from "framer-motion";
import siteLogo from "@/assets/logo-nathalie-barach.png";

const navLinks = [
  { label: "Accueil", to: "/" },
  { label: "À propos", to: "/a-propos" },
  { label: "Arrêter le tabac", to: "/arreter-le-tabac" },
  { label: "Thérapies", to: "/therapies" },
  { label: "FAQ", to: "/faq" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // Close mobile menu on route change (also handles same-route clicks)
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      {/* Marquee top banner */}
      <Link to="/blog/arret-tabac-hypnose-mutuelle-remboursement" className="block bg-accent text-accent-foreground overflow-hidden whitespace-nowrap py-3 hover:brightness-105 transition-all cursor-pointer">
        <div className="flex w-max animate-[marquee_60s_linear_infinite]">
          {[0, 1].map((i) => (
            <span key={i} className="font-body text-xs font-semibold tracking-wide px-1">
              {Array(4)
                .fill("Vos séances d'hypnothérapie peuvent être remboursées par votre mutuelle — Renseignez-vous   •   ")
                .join("")}
            </span>
          ))}
        </div>
      </Link>

      <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" onClick={() => { setOpen(false); window.scrollTo(0, 0); }}>
            <img src={siteLogo} alt="Nathalie Barach" width={40} height={40} decoding="async" className="h-10 w-10 rounded-full object-cover" />
            <span className="font-body text-xs sm:text-sm text-muted-foreground leading-tight max-w-[160px]">
              Hypnose · DTMA · Coaching
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => { window.scrollTo(0, 0); }}
                className={`text-sm font-body transition-colors hover:text-primary ${
                  pathname === l.to ? "text-primary font-semibold" : "text-foreground/70"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="px-5 py-2 rounded-full bg-accent text-accent-foreground text-sm font-body font-semibold hover:brightness-105 transition-all"
            >
              Prendre rendez-vous
            </Link>
          </nav>

          {/* Phone + mobile toggle */}
          <div className="flex items-center gap-3 lg:hidden">
            <a href="tel:+33628436977" className="text-primary" aria-label="Appeler">
              <Phone size={20} />
            </a>
            <button
              onClick={() => setOpen(!open)}
              aria-label="Menu"
              className="text-foreground/70"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

      </header>

      {/* Mobile menu — fullscreen overlay (no exit animation: it could leave a stuck overlay on navigation) */}
      {open && (
          <motion.nav
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg:hidden fixed inset-0 z-[999] flex flex-col bg-background"
          >
            {/* Top bar: logo + close */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-2">
                <img src={siteLogo} alt="Nathalie Barach" width={40} height={40} decoding="async" className="h-10 w-10 rounded-full object-cover" />
                <span className="font-body text-xs text-muted-foreground leading-tight max-w-[160px]">
                  Hypnose · DTMA · Coaching
                </span>
              </Link>
              <button onClick={() => setOpen(false)} aria-label="Fermer le menu" className="text-foreground/70 p-1">
                <X size={28} />
              </button>
            </div>

            {/* Nav links */}
            <div className="flex flex-col flex-1 px-6 pt-6 gap-1">
              {navLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => { setOpen(false); window.scrollTo(0, 0); }}
                  className={`text-lg font-body py-3 transition-colors rounded-lg px-4 ${
                    pathname === l.to
                      ? "text-primary font-semibold bg-muted"
                      : "text-foreground/80 hover:bg-muted/50"
                  }`}
                >
                  {l.label}
                </Link>
              ))}
            </div>

            {/* Bottom: phone + CTA */}
            <div className="px-6 pb-10 pt-6 border-t border-border space-y-5">
              <a
                href="tel:+33628436977"
                className="flex items-center gap-2.5 text-primary font-body font-semibold text-base"
              >
                <Phone size={20} /> 06 28 43 69 77
              </a>
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="block w-full py-4 rounded-full bg-accent text-accent-foreground text-center text-base font-body font-semibold"
              >
                Prendre rendez-vous
              </Link>
            </div>
          </motion.nav>
      )}
    </>
  );
};

export default SiteHeader;
